<template>
  <v-card flat class="pa-2">
    <!-- Greeting Banner -->
    <v-card class="rounded-xl mb-4 white--text" :class="bannerClass" elevation="3">
      <v-card-text class="pa-6">
        <v-row align="center" no-gutters>
          <v-col cols="auto" class="mr-4">
            <v-avatar size="68" color="white" class="elevation-3">
              <v-icon size="42" :color="bannerIconColor">mdi-account-tie</v-icon>
            </v-avatar>
          </v-col>
          <v-col>
            <div class="text-h5 font-weight-bold">{{ greeting }}, {{ userName }}</div>
            <div class="text-subtitle-1 mt-1" style="opacity:0.92">
              <template v-if="totalPending === 0">
                No tienes órdenes pendientes de aprobación
              </template>
              <template v-else>
                Tienes
                <span class="text-h6 font-weight-black"> {{ totalPending }} </span>
                {{ totalPending === 1 ? 'orden pendiente' : 'órdenes pendientes' }} de aprobación
              </template>
            </div>
          </v-col>
          <!-- KPI: monto total -->
          <v-col cols="auto" class="text-right mr-6" v-if="totalPending > 0">
            <div class="text-caption white--text" style="opacity:0.8">MONTO TOTAL</div>
            <div v-for="(amount, code) in amountsByCurrency" :key="code" class="text-subtitle-1 font-weight-bold">
              {{ code }} {{ formatAmount(amount) }}
            </div>
          </v-col>
          <v-col cols="auto" class="d-none d-md-flex">
            <v-icon size="90" style="opacity:0.10">mdi-clipboard-check-multiple-outline</v-icon>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <template v-if="totalPending > 0">
      <!-- Summary chips by BU -->
      <v-row class="mb-3" v-if="buSummary.length > 0">
        <v-col v-for="bu in buSummary" :key="bu.name" cols="auto">
          <v-chip color="primary" outlined small>
            <v-icon left small>mdi-office-building</v-icon>
            <strong>{{ bu.name }}</strong>&nbsp;—&nbsp;{{ bu.total }} órdenes
          </v-chip>
        </v-col>
      </v-row>

      <!-- Bar Chart -->
      <v-card outlined class="rounded-xl pa-4 mb-4">
        <div class="d-flex align-center mb-3">
          <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
          <span class="text-subtitle-1 font-weight-bold grey--text text--darken-2">
            Pendientes por Sucursal
          </span>
        </div>
        <apexchart
          type="bar"
          height="260"
          :options="chartOptions"
          :series="chartSeries"
        />
      </v-card>

      <!-- Pending Orders Table -->
      <v-card outlined class="rounded-xl">
        <v-toolbar flat dense class="rounded-t-xl">
          <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
          <span class="font-weight-bold">Órdenes Pendientes de Aprobación</span>
          <v-spacer />
          <v-chip small color="primary" class="font-weight-bold">{{ tableRows.length }}</v-chip>
        </v-toolbar>
        <v-data-table
          :headers="tableHeaders"
          :items="tableRows"
          :sort-by="['days_pending']"
          :sort-desc="[true]"
          dense
          class="elevation-0"
        >
          <!-- Order ID clickable -->
          <template v-slot:[`item.order_id`]="{ item }">
            <span
              class="primary--text font-weight-bold text-decoration-underline"
              style="cursor:pointer"
              @click="openOrder(item.order_id)"
            >
              {{ formatOrderId(item.order_id) }}
            </span>
          </template>

          <!-- Amount -->
          <template v-slot:[`item.amount`]="{ item }">
            <span class="font-weight-medium">
              {{ item.currency_code }} {{ formatAmount(item.amount) }}
            </span>
          </template>

          <!-- Days pending with color -->
          <template v-slot:[`item.days_pending`]="{ item }">
            <v-chip
              x-small
              :color="ageColor(item.days_pending)"
              dark
              class="font-weight-bold"
            >
              <v-icon x-small left>{{ ageIcon(item.days_pending) }}</v-icon>
              {{ item.days_pending }}d
            </v-chip>
          </template>

          <!-- Status chip -->
          <template v-slot:[`item.last_status_name`]="{ item }">
            <v-chip x-small outlined color="grey darken-1">
              {{ item.last_status_name }}
            </v-chip>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </v-card>
</template>

<script>
export default {
  props: {
    series: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
    all_data: { type: Array, default: () => [] },
    detail_orders: { type: Array, default: () => [] },
  },
  computed: {
    greeting() {
      const h = new Date().getHours();
      if (h >= 5 && h < 12) return 'Buenos días';
      if (h >= 12 && h < 19) return 'Buenas tardes';
      return 'Buenas noches';
    },
    bannerClass() {
      const h = new Date().getHours();
      if (h >= 5 && h < 12) return 'banner-morning';
      if (h >= 12 && h < 19) return 'banner-afternoon';
      return 'banner-night';
    },
    bannerIconColor() {
      const h = new Date().getHours();
      if (h >= 5 && h < 12) return 'blue darken-2';
      if (h >= 12 && h < 19) return 'orange darken-2';
      return 'indigo darken-2';
    },
    userName() {
      const user = this.$store.state.auth?.user?.user;
      return user?.fullname?.split(' ').slice(0, 2).join(' ') || 'Usuario';
    },
    totalPending() {
      return this.all_data.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
    },
    amountsByCurrency() {
      const totals = {};
      for (const order of this.detail_orders) {
        const currency = order.currency_code || 'DOP';
        if (!totals[currency]) totals[currency] = 0;
        
        const details = Array.isArray(order.order_details) ? order.order_details : [];
        for (const d of details) {
          const subtotal = (d.quantity || 0) * (d.price || 0);
          totals[currency] += subtotal - subtotal * (d.discount || 0);
        }
      }
      return totals;
    },
    buSummary() {
      const map = {};
      for (const item of this.all_data) {
        const key = item.business_unit_name || 'Sin BU';
        if (!map[key]) map[key] = { name: key, total: 0 };
        map[key].total += parseInt(item.quantity || 0);
      }
      return Object.values(map);
    },
    tableHeaders() {
      return [
        { text: '# Orden',      value: 'order_id',           sortable: true  },
        { text: 'Proveedor',    value: 'entity_name',         sortable: true  },
        { text: 'Sucursal',     value: 'location_name',       sortable: true  },
        { text: 'Monto',        value: 'amount',              sortable: true  },
        { text: 'Días',         value: 'days_pending',        sortable: true  },
        { text: 'Estado',       value: 'last_status_name',    sortable: false },
      ];
    },
    tableRows() {
      return this.detail_orders.map(order => {
        const details = Array.isArray(order.order_details) ? order.order_details : [];
        const amount = details.reduce((acc, d) => {
          const sub = (d.quantity || 0) * (d.price || 0);
          return acc + sub - sub * (d.discount || 0);
        }, 0);
        return { ...order, amount };
      });
    },
    chartSeries() {
      return [{
        name: 'Órdenes Pendientes',
        data: this.all_data.map(item => ({
          x: item.location_name || `Loc ${item.location_id}`,
          y: parseInt(item.quantity || 0),
        })),
      }];
    },
    chartOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: {
          bar: { columnWidth: '55%', borderRadius: 6, distributed: true, dataLabels: { position: 'top' } },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => val,
          offsetY: -20,
          style: { fontSize: '12px', colors: ['#424242'] },
        },
        legend: { show: false },
        xaxis: { labels: { rotate: -30, style: { fontSize: '11px' } } },
        yaxis: { title: { text: 'Cantidad' }, labels: { formatter: (val) => Math.round(val) } },
        tooltip: {
          y: { formatter: (val) => `${val} órdenes` },
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            const item = this.all_data[dataPointIndex];
            if (!item) return '';
            return `<div class="px-3 py-2">
              <div><strong>${item.business_unit_name || ''}</strong></div>
              <div>${item.location_name || ''}</div>
              <div class="mt-1"><strong>${series[seriesIndex][dataPointIndex]}</strong> órdenes</div>
            </div>`;
          },
        },
        colors: ['#1565C0','#2E7D32','#E65100','#6A1B9A','#00838F','#B71C1C','#558B2F','#4E342E'],
      };
    },
  },
  methods: {
    ageColor(days) {
      if (days <= 2) return 'success';
      if (days <= 5) return 'warning';
      return 'error';
    },
    ageIcon(days) {
      if (days <= 2) return 'mdi-clock-check-outline';
      if (days <= 5) return 'mdi-clock-alert-outline';
      return 'mdi-clock-remove-outline';
    },
    formatAmount(val) {
      return new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
    },
    formatOrderId(id) {
      return String(id).padStart(12, '0');
    },
    openOrder(order_id) {
      this.showOrder(order_id, true, 1);
    },
  },
};
</script>

<style scoped>
.banner-morning {
  background: linear-gradient(135deg, #1565C0 0%, #42A5F5 100%) !important;
}
.banner-afternoon {
  background: linear-gradient(135deg, #BF360C 0%, #FF7043 100%) !important;
}
.banner-night {
  background: linear-gradient(135deg, #1A237E 0%, #5C6BC0 100%) !important;
}
</style>